<template>
  <v-row class="pageform match-height">
    <v-col cols="12" v-if="isLoading">
      <div class="text-center">
        <v-progress-circular indeterminate></v-progress-circular>
      </div>
    </v-col>
    <v-col cols="12" v-else>
      <v-card>
        <v-card-title>{{ getPageTitle() }}</v-card-title>
        <v-card-text>
          <v-form v-on:submit.prevent="submit" ref="form">
            <div>
              <label for="image">{{ $t("Kuva") }}</label>
              <br />
              <small class="form-text text-muted">{{
                $t(
                  "Raahaa kuva tähän tai klikkaa tästä ja valitse kuva tiedostoista."
                )
              }}</small>
              <br />
              <croppa initial-size="contain" accept=".jpeg,.jpg,.png" @file-type-mismatch="onFileTypeMismatch"
                :disabled="croppaEnabled === false" v-model="myCroppa" :width="600" :height="400"
                :initial-image="companyPage.image" :placeholder="$t('Valitse kuva')" @image-remove="removeImage"
                @new-image-drawn="imageLoaded"></croppa>
              <CroppaControls :croppa="myCroppa" v-if="showCroppaControls" />

              <div v-if="companyPage.image">
                <small class="form-text text-muted">{{
                  $t("Jos haluat päivittää kuvan, poista ensin olemassa oleva kuva")
                }}</small>
              </div>
            </div>

            <div class="mt-8 mb-3">
              <legend class="v-label mb-2"> {{ $t('Julkaise ryhmään') }} </legend>
              <v-checkbox v-for="group in groups" v-model="companyPage.groups" :key="group.id" :value="group.id"
                :disabled="group.disabled" :label="group.name" dense class="ma-0 pa-0" @change="groupsChanged"
                :style="{ 'opacity': group.group_type !== 1 && mainGroupSelected ? '0.5' : '1' }" />

              <v-input v-model="companyPage.groups" :rules="[required]" class="ml-4 mb-4" hidden
                hide-details="auto"></v-input>

            </div>
            <br>
            <v-card outlined>
              <v-tabs v-model="tab" grow>
                <v-tab v-for="lang in tenant.languages" :key="'langtab-' + lang.code">
                  <img :src="lang.icon" style="max-height: 20px" />
                  &nbsp;&nbsp;{{ lang.name }}
                  &nbsp;
                  <v-icon v-if="langOk(companyPage.translations, lang.code, [
                    'title',
                    'text',
                  ])
                  " color="success">
                    {{ icons.mdiCheck }}
                  </v-icon>
                  <v-icon v-else color="error">
                    {{ icons.mdiBlockHelper }}
                  </v-icon>
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="tab">
                <v-tab-item v-for="lang in tenant.languages" :key="'langtabitem-' + lang.code">
                  <div style="padding: 20px">
                    <img :src="lang.icon" style="max-height: 14px" />
                    <br />
                    <br />
                    <v-text-field outlined :id="'news-title-' + lang.code"
                      v-model="companyPage.translations[lang.code].title" :label="$t('Otsikko')" hide-details
                      class="mb-4" />
                    <div>
                      <quill-editor height="400" ref="myQuillEditor" v-model="companyPage.translations[lang.code].text"
                        :options="editorOption" />
                    </div>
                    <div class="mt-4" v-if="tenant.settings.auto_translations_enabled">
                      <AutoTranslateButton :current="lang" :languages="tenant.languages" :is-translating="isTranslating"
                        @click="translate" translation-errors="translationErrors" :errors="translationErrors">
                      </AutoTranslateButton>
                    </div>
                  </div>
                </v-tab-item>
              </v-tabs-items>
              <v-input class="ml-4 mb-4" hidden hide-details="auto" :rules="[validateLangs]"></v-input>
            </v-card>

            <v-card outlined class="mt-5">


              <v-card-title>{{ $t('Liitteet') }}</v-card-title>
              <v-card-text>
                {{ $t('attachment_lang_tip') }}

              </v-card-text>
              <v-tabs v-model="attachmentsTab" grow>
                <v-tab key="attachment-tab-null">
                  {{ $t('Yhteiset liitteet') }}
                </v-tab>
                <v-tab v-for="lang in tenant.languages" :key="'attachment-tab-' + lang.code">
                  <img :src="lang.icon" style="max-height: 20px" class="mr-3" />
                  {{ lang.name }}
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="attachmentsTab">
                <v-tab-item key='attachment-tab-null'>
                  <div style="padding: 20px">
                    <template v-if="companyPage.id">
                      <v-list>
                        <draggable v-model="attachments['common']" tag="div" handle=".handle" ghost-class="ghost"
                          @end="sort(attachments['common'])">
                          <v-list-item v-for="attachment in attachments['common']" :key="attachment.id">
                            <v-list-item-icon class="d-flex">
                              <v-icon color="primary" class="handle mr-3">
                                {{ icons.mdiCursorMove }}
                              </v-icon>
                              <v-icon color="primary">
                                {{ icons.mdiAttachment }}
                              </v-icon>
                            </v-list-item-icon>
                            <v-list-item-content style="cursor: pointer" @click="downloadAttachment(attachment)">
                              {{ attachment.name }}
                            </v-list-item-content>
                            <v-list-item-icon @click="removeAttachment(attachment)" style="cursor: pointer">
                              <v-icon color="secondary">
                                {{ icons.mdiDeleteOutline }}
                              </v-icon>
                            </v-list-item-icon>
                          </v-list-item>
                        </draggable>
                      </v-list>
                      <v-file-input outlined ref="attachmentInput" :label="$t('Lisää uusi liitetiedosto')"
                        :loading="isUploading" @change="(file) => saveAttachment(file, null)"
                        accept=".jpeg,.jpg,.png, .pdf, .doc, .docx, .ppt, .pptx, .xlsx, .xls"></v-file-input>
                    </template>
                    <template v-else>
                      {{ $t("Voit lisätä sivulle liitteitä tallennuksen jälkeen") }}
                    </template>
                  </div>
                </v-tab-item>
                <v-tab-item v-for="lang in tenant.languages" :key="'attachment-tab-' + lang.code">
                  <div style="padding: 20px">
                    <template v-if="companyPage.id">
                      <v-list>
                        <draggable v-model="attachments[lang.code]" tag="div" handle=".handle" ghost-class="ghost"
                          @end="sort(attachments[lang.code])">
                          <v-list-item v-for="attachment in attachments[lang.code]" :key="attachment.id">
                            <v-list-item-icon>
                              <v-icon color="primary" class="handle mr-3">
                                {{ icons.mdiCursorMove }}
                              </v-icon>
                              <v-icon color="primary">
                                {{ icons.mdiAttachment }}
                              </v-icon>
                            </v-list-item-icon>
                            <v-list-item-content style="cursor: pointer" @click="downloadAttachment(attachment)">
                              {{ attachment.name }}
                            </v-list-item-content>
                            <v-list-item-icon @click="removeAttachment(attachment)" style="cursor: pointer">
                              <v-icon color="secondary">
                                {{ icons.mdiDeleteOutline }}
                              </v-icon>
                            </v-list-item-icon>
                          </v-list-item>
                        </draggable>
                      </v-list>
                      <v-file-input outlined ref="attachmentInput" :loading="isUploading"
                        :label="$t('Lisää uusi liitetiedosto') + ' (' + lang.name + ')'"
                        @change="(file) => saveAttachment(file, lang.code)"
                        accept=".jpeg,.jpg,.png, .pdf, .doc, .docx, .ppt, .pptx, .xlsx, .xls"></v-file-input>
                    </template>
                    <template v-else>
                      {{ $t("Voit lisätä sivulle liitteitä tallennuksen jälkeen") }}
                    </template>
                  </div>
                </v-tab-item>
              </v-tabs-items>

            </v-card>

            <v-card outlined class="mt-4">
              <v-card-title>{{ $t("Video") }}</v-card-title>
              <v-card-text>
                <v-switch dense hide-details="auto" v-model="hasVideo" :label="$t('Lisää sivulle video')"
                  @change="companyPage.videoUrl = ''" class="mb-2 mt-0"></v-switch>

                <v-text-field v-if="hasVideo" :label="$t('Videotiedoston url-osoite')" v-model="companyPage.videoUrl"
                  append-outer-icon="mdi-help" outlined>
                </v-text-field>
              </v-card-text>
            </v-card>

            <v-card outlined class="mt-4" v-if="tenant.settings.acknowledgments_enabled">
              <v-card-title>{{ $t("Lukukuittaus") }}</v-card-title>
              <v-card-text>
                <v-switch dense hide-details="auto" v-model="companyPage.require_acknowledgment"
                  :label="$t('Vaadi lukukuittaus')" class="mb-2 mt-0"></v-switch>

                <div v-if="companyPage.require_acknowledgment" class="mt-4">
                  <v-sheet outlined>
                    <v-tabs v-model="acknowledgmentTab" grow>
                      <v-tab v-for="lang in tenant.languages" :key="'ack-tab-' + lang.code">
                        <img :src="lang.icon" style="max-height: 20px" />
                        &nbsp;&nbsp;{{ lang.name }}
                      </v-tab>
                    </v-tabs>

                    <v-tabs-items v-model="acknowledgmentTab">
                      <v-tab-item v-for="lang in tenant.languages" :key="'ack-tabitem-' + lang.code">
                        <div style="padding: 20px">
                          <br />
                          <v-text-field outlined :label="$t('Selite (valinnainen)')"
                            v-model="companyPage.translations[lang.code].acknowledgment_description" :disabled="!langOk(companyPage.translations, lang.code, [
                              'title',
                              'text',
                            ])" :hint="$t('Teksti näytetään käyttäjille lukukuittauksen yhteydessä')" persistent-hint>
                          </v-text-field>
                        </div>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-sheet>
                  <br>

                  <v-text-field v-if="companyPage.require_acknowledgment" :label="$t('Versio')"
                    :hint="$t('Jos nostatte versionumeroa, käyttäjiltä vaaditaan sisällölle uudelleen lukukuittaus')"
                    :persistent-hint="true" v-model="companyPage.acknowledgment_version" type="number" min="1" outlined
                    class="mt-2 mb-2"></v-text-field>
                </div>
              </v-card-text>
            </v-card>

            <v-switch v-model="companyPage.published" inset persistent-hint :hint="companyPage.published
              ? $t('Sivu julkaistaan heti')
              : $t('Sivu tallennetaan luonnoksena')
              " :label="$t('Julkaise')"></v-switch>
            <br />
            <v-alert color="error" text v-if="!formIsValid">
              {{ $t("Tarkista lomakkeen tiedot") }}
            </v-alert>
            <v-btn color="error" v-if="companyPage.id" @click="removePage" class="mr-3">{{ $t("Poista") }}</v-btn>
            <v-btn color="primary" @click="submit" :loading="isSubmitting">{{ $t("Tallenna") }}</v-btn>
          </v-form>
        </v-card-text>
      </v-card>
      <v-snackbar v-model="translationCompleted" color="success">
        <template #default>
          <v-icon>{{ icons.mdiCheck }}</v-icon>
          {{ $t('Käännös suoritettu. Varmista käännöksen oikeellisuus ennen tallentamista.') }}
        </template>
      </v-snackbar>
    </v-col>
  </v-row>
</template>

<script>
import { computed, onMounted, reactive, ref, nextTick, watch } from "@vue/composition-api";
import adminApi from "@/api/admin";
import { useRouter } from "@/@core/utils";
import useAuth from "@/useAuth";
import { useTranslations } from "@/@core/libs/i18n";
import {
  convertTranslationsHtmlToMarkdown,
  convertTranslationMarkdownToHtml,
  getValidTranslations,
  langOk,
  translateObjectTranslations,
} from "@/@core/utils/translation_helpers";
import {
  mdiCheck,
  mdiBlockHelper,
  mdiClockOutline,
  mdiCalendar,
  mdiAttachment,
  mdiCursorMove,
  mdiDeleteOutline,
} from "@mdi/js";
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import "quill/dist/quill.bubble.css";
import { quillEditor } from "vue-quill-editor";
import "vue-croppa/dist/vue-croppa.css";
import download from "downloadjs";
import useMenus from "@/@core/utils/useMenus";
import { i18n } from "@/plugins/i18n";
import ImageUploader from 'quill-image-uploader';
import Quill from 'quill'
import 'quill-paste-smart';
import { required } from "@/@core/utils/validation";
Quill.register("modules/imageUploader", ImageUploader);
import AutoTranslateButton from "@/components/AutoTranslateButton.vue";
import draggable from "vuedraggable";
import { MAX_FILE_SIZE } from "@core/utils/constants";
import CroppaControls from "@/components/CroppaControls.vue";

export default {
  props: ["id", "parent"],
  components: {
    quillEditor,
    AutoTranslateButton,
    draggable,
    CroppaControls
  },
  setup(props) {
    const isLoading = ref(true);
    const { router, route } = useRouter();
    const { tenant } = useAuth();
    const { getTenantTerm } = useMenus();
    const { t } = useTranslations();
    const formIsValid = ref(true);
    const attachmentInput = ref(null);
    const tab = ref(null);
    const attachmentsTab = ref(null);
    const acknowledgmentTab = ref(null);
    const form = ref(null);
    const isSubmitting = ref(false);
    const { user } = useAuth();
    const parentPageGroups = ref([]);
    const croppaEnabled = ref(true);
    const forceUpdateTranslationsKey = ref(new Date().toString());
    const isTranslating = ref(false);
    const translationErrors = ref([]);
    const translationCompleted = ref(false);
    const isUploading = ref(false);

    const forceUpdateTranslations = () => {
      forceUpdateTranslationsKey.value = new Date().toString();
    }

    const editorOption = {
      modules: {
        clipboard: {
          keepSelection: true,
          substituteBlockElements: false,
          magicPasteLinks: true,
        },
        toolbar: [
          ["bold", "italic"],
          [{ 'list': 'ordered' }, { 'list': 'bullet' }],
          ["link", "image"],
        ],
        imageUploader: {
          upload: (file) => {
            return new Promise(async (resolve, reject) => {

              const res = await adminApi.uploadImage(file);

              if (res.ok) {
                resolve(res.data);
              }
              else {
                reject("Image upload failed");
              }
            });
          },
        },
      },
    };
    const myCroppa = ref({});
    const myCroppaInitialMedadata = {
      startX: 0,
      startY: 0,
      scale: 1,
      orientation: 1,
    };

    const companyPage = reactive({
      id: null,
      translations: {},
      published: true,
      image: null,
      ui_order: null,
      videoUrl: null,
      groups: [],
      require_acknowledgment: false,
      acknowledgment_version: 1
    });
    const imageRemoved = ref(false);
    const attachments = ref({});
    tenant.value.languages.forEach((lang) => {
      attachments.value[lang.code] = [];
    });
    attachments.value["common"] = []; // For language-independent attachments
    //const attachments = ref([]);
    const hasVideo = ref(false);
    let hasChildren = false;

    tenant.value.languages.forEach((lang) => {
      companyPage.translations[lang.code] = {
        title: "",
        text: "",
        acknowledgment_description: "",
      };
    });

    const translate = async (fromLangCode, toLangs) => {

      translationErrors.value = [];

      if (langOk(companyPage.translations, fromLangCode, ["text", "title"])) {
        isTranslating.value = true;
        const errors = await translateObjectTranslations(companyPage.translations, fromLangCode, toLangs, ["text", "title"]);
        translationErrors.value = errors;
        forceUpdateTranslations();
        isTranslating.value = false;

        if (errors.length == 0) {
          translationCompleted.value = true;
        }

      }
      else {
        alert("Otsikko ja teksti on pakollinen ennen käännöstä");
      }
    }

    const fetchParentGroups = async (pageId, stopLoading) => {
      const response = await adminApi.getPageGroups(pageId);
      parentPageGroups.value = response.data;
      if (stopLoading) {
        isLoading.value = false;
      }
    }

    const fetchPage = async (id) => {
      const response = await adminApi.getPage(id);
      if (response.ok) {

        if (response.data.parent_page) {
          await fetchParentGroups(response.data.parent_page, false);
        }

        companyPage.id = response.data.id;
        companyPage.published = response.data.published;
        companyPage.ui_order = response.data.ui_order;
        companyPage.groups = response.data.groups;
        companyPage.require_acknowledgment = response.data.require_acknowledgment || false;
        companyPage.acknowledgment_version = response.data.acknowledgment_version || 1;

        // Translations
        companyPage.translations = {};
        tenant.value.languages.forEach((lang) => {
          if (response.data.translations[lang.code]) {
            companyPage.translations[lang.code] = response.data.translations[lang.code];
          } else {
            companyPage.translations[lang.code] = {
              title: "",
              text: "",
              acknowledgment_description: "",
            };
          }
        });
        convertTranslationMarkdownToHtml(companyPage.translations, ["text"]);

        // Image
        if (response.data.image) {
          const fullSize = response.data.image["full_size"];
          if (fullSize) {
            companyPage.image = fullSize;
            croppaEnabled.value = false;
          }
        }

        // Attachments
        response.data.attachments.forEach((attachment) => {
          if (attachment.attachment_type == 2) {
            hasVideo.value = true;
            companyPage.videoUrl = attachment.url;
          } else {
            attachments.value[attachment.language ?? 'common'].push(attachment);
            //attachments.value.push(attachment);
          }
        });

        isLoading.value = false;
        // myCroppa
        if (companyPage.image) {
          nextTick(() => {
            myCroppa.value.refresh();
          });
        }

        hasChildren =
          response.data.children && response.data.children.length > 0;
      } else {
        alert(response.error);
        isLoading.value = false; s
      }

    };

    const submit = async () => {
      formIsValid.value = true;
      if (form.value.validate()) {
        isSubmitting.value = true;
        const payload = Object.assign({}, companyPage);

        // Translations
        payload.translations = getValidTranslations(companyPage.translations, [
          "title",
          "text",
        ]);
        // convertTranslationsHtmlToMarkdown(payload.translations, ["text"]);

        // Parent
        let parent = null;
        if (route.value.query.parent) parent = route.value.query.parent;

        // Category
        let category = null;
        if (route.value.query.category) category = route.value.query.category;

        // Image
        let newImageName = "";
        let imageBlob = null;
        let removeImage = false;
        if (myCroppa.value.hasImage()) {
          if (myCroppa.value.chosenFile) {
            imageBlob = await myCroppa.value.promisedBlob(
              myCroppa.value.chosenFile.type,
              0.8
            );
            newImageName = myCroppa.value.chosenFile.name;
          }
        } else if (imageRemoved) {
          removeImage = true;
        }

        const response = await adminApi.savePage(
          payload,
          parent,
          imageBlob,
          newImageName,
          removeImage,
          category
        );

        if (response.ok) {
          const categoryId = route.value.query.category;
          router.push({ name: "admin-page-list", query: categoryId ? { category: categoryId } : {} });
        } else {
          alert(response.error);
        }
      } else {
        formIsValid.value = false;
      }
    };

    const removePage = async () => {
      if (hasChildren) {
        alert(i18n.t("Sivua ei voida poistaa, poista ensin sivun alasivut!"));
        return;
      }
      if (confirm(i18n.t("Haluatko varmasti poistaa sivun?"))) {
        const response = await adminApi.deletePage(companyPage.id);
        if (response.ok) {
          const categoryId = route.value.query.category;
          router.replace({ name: "admin-page-list", query: categoryId ? { category: categoryId } : {} });
        } else {
          alert(response.error);
        }
      }
    };

    const saveAttachment = async (file, langCode) => {
      if (file) {

        if (file.size > MAX_FILE_SIZE) {
          alert(i18n.t('Tiedoston maksimikoko on 10MB'));
          return;
        }

        isUploading.value = true;
        const response = await adminApi.savePageAttachment(
          companyPage.id,
          file,
          langCode
        );

        if (response.status == 201) {
          const atts = structuredClone(attachments.value);
          atts[langCode ?? 'common'] = [response.data, ...atts[langCode ?? 'common']];
          attachments.value = atts;
        }
        else {
          alert(response.error);
        }
        attachmentInput.value = "";
        isUploading.value = false;
      }
    };

    const downloadAttachment = async (attachment) => {
      const response = await adminApi.downloadPageAttachment(
        companyPage.id,
        attachment.id
      );

      if (response.ok) {
        download(response.data, attachment.name);
      } else {
        alert(response.error);
      }
    };

    const removeAttachment = async (attachment) => {
      if (confirm(i18n.t("Haluatko varmasti poistaaa liitteen?"))) {

        const response = await adminApi.removePageAttachment(
          companyPage.id,
          attachment.id
        );

        if (response.ok) {
          const atts = structuredClone(attachments.value);
          atts[attachment.language ?? 'common'] = atts[attachment.language ?? 'common'].filter(
            (a) => a.id !== attachment.id
          );
          attachments.value = atts;
        } else {
          alert(response.error);
        }
      }
    };

    const onFileTypeMismatch = (file) => {
      alert(
        i18n.t(
          "Tiedostomuoto ei ole tuettu. Tuettuja tiedostomuotoja ovat jpeg sekä png."
        )
      );
    };

    const imageLoaded = () => {
      imageRemoved.value = false;
    };

    const removeImage = () => {
      companyPage.image = null;
      imageRemoved.value = true;
      croppaEnabled.value = true;
    };


    const validateLangs = () => {
      let valid = false;
      tenant.value.languages.forEach((lang) => {
        if (langOk(companyPage.translations, lang.code, ["text", "title"]))
          valid = true;
      });

      if (valid) {
        return true;
      } else {
        return i18n.t("Otsikko tai teksti puuttuu");
      }
    };

    const getPageTitle = () => {
      const tenantTerm = getTenantTerm(
        "our_company",
        tenant.value.terms?.translations,
        i18n.locale
      );
      return tenantTerm ?? i18n.t("Meidän yritys");
    };

    onMounted(() => {
      if (props.id) {
        fetchPage(props.id);
      }
      else if (route.value.query.parent) {
        fetchParentGroups(route.value.query.parent, true);
      }
      else {
        isLoading.value = false;
      }
    });

    const groups = computed(() => {
      const mainGroup = getMainGroup();
      const allGroupsAllowed = parentPageGroups.value.length == 0 || parentPageGroups.value.includes(mainGroup.id);

      return user.value.admin_groups.map(g => {
        return {
          id: g.id,
          name: g.name,
          group_type: g.group_type,
          disabled: !allGroupsAllowed && !parentPageGroups.value.includes(g.id)
        };
      });
    });

    const mainGroupSelected = computed(() => {
      const mainGroup = getMainGroup();
      return companyPage.groups.includes(mainGroup.id);
    });

    const groupsChanged = () => {
      const mainGroup = getMainGroup();
      if (companyPage.groups.includes(mainGroup.id)) {
        companyPage.groups = [mainGroup.id];
      }
    }

    const getMainGroup = () => {
      return user.value.admin_groups.find(g => g.group_type == 1);
    }

    const sort = async (atts) => {
      if (atts.length) {
        const lang = atts[0].language ?? 'common';

        const ids = atts.map((a) => a.id);

        const res = await adminApi.sortPageAttachments(props.id, ids);

        const sortedAttachments = structuredClone(attachments.value);
        sortedAttachments[lang] = [...atts];
        attachments.value = sortedAttachments;

        forceUpdateTranslations();
      }
    }

    const showCroppaControls = computed(() => {
      if (!myCroppa.value || Object.keys(myCroppa.value).length === 0) return false;

      return croppaEnabled.value && myCroppa.value.hasImage();
    })

    return {
      isLoading,
      showCroppaControls,
      sort,
      groups,
      parentPageGroups,
      getPageTitle,
      companyPage,
      myCroppa,
      attachmentsTab,
      acknowledgmentTab,
      isUploading,
      tenant,
      formIsValid,
      tab,
      form,
      editorOption,
      myCroppaInitialMedadata,
      attachments,
      hasVideo,
      required,
      croppaEnabled,
      forceUpdateTranslationsKey,
      onFileTypeMismatch,
      translationErrors,
      isTranslating,
      translate,
      imageLoaded,
      removeImage,
      langOk,
      isSubmitting,
      submit,
      translationCompleted,
      removePage,
      saveAttachment,
      downloadAttachment,
      removeAttachment,
      validateLangs,
      mainGroupSelected,
      groupsChanged,
      icons: {
        mdiCheck,
        mdiBlockHelper,
        mdiClockOutline,
        mdiCalendar,
        mdiAttachment,
        mdiCursorMove,
        mdiDeleteOutline,
      },
    };
  },
};
</script>


<style>
.pageform .ql-editor p {
  font-size: 1.2em;
  line-height: 1.8em;
}

.pageform .ql-editor {
  min-height: 200px;
}
</style>
